export default function ConnectiveLayer() {
  const nodes = [
    { id: "demand", label: "DEMAND", sub: "CBPs · iGOT signals · district asks", color: "#D95D39", x: 20, y: 20 },
    { id: "delivery", label: "DELIVERY", sub: "Programmes · faculty · cohorts", color: "#0077B6", x: 50, y: 65 },
    { id: "knowledge", label: "KNOWLEDGE", sub: "Case studies · modules · pedagogy", color: "#4C956C", x: 80, y: 20 },
  ];
  return (
    <section id="vision-layer" data-testid="connective-layer-section"
             className="relative bg-cream py-28 md:py-36 overflow-hidden">
      <div className="absolute inset-0 bg-lattice" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="font-mono-data text-xs tracking-[0.3em] uppercase text-terracotta mb-4">01 · Connective Intelligence</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Three flows. <span className="italic">One coherent system.</span>
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-navy/75">
              Today, training demand sits in one silo, programme design in another, institutional knowledge in a third.
              UNNATI is the working layer that makes them visible to each other — so every signal, cohort and case study
              compounds into national capacity.
            </p>
            <div className="mt-8 space-y-3">
              {nodes.map(n => (
                <div key={n.id} data-testid={`flow-legend-${n.id}`} className="flex items-start gap-3 border-l-2 pl-4 py-1" style={{borderColor: n.color}}>
                  <div>
                    <div className="font-mono-data text-xs tracking-[0.2em]" style={{color: n.color}}>{n.label}</div>
                    <div className="text-sm text-navy/80">{n.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="relative aspect-[4/3] w-full bg-white border border-navy/10">
              <div className="absolute inset-0 chakra-radial opacity-40" />
              <svg viewBox="0 0 800 600" className="w-full h-full relative">
                <line x1="160" y1="140" x2="640" y2="140" stroke="#0B172A" strokeOpacity="0.15" strokeDasharray="3 5" />
                <line x1="160" y1="140" x2="400" y2="430" stroke="#0B172A" strokeOpacity="0.15" strokeDasharray="3 5" />
                <line x1="640" y1="140" x2="400" y2="430" stroke="#0B172A" strokeOpacity="0.15" strokeDasharray="3 5" />

                <path d="M 160 140 Q 400 60 640 140" stroke="#D95D39" strokeWidth="2" fill="none" className="connective-line" />
                <path d="M 640 140 Q 560 320 400 430" stroke="#4C956C" strokeWidth="2" fill="none" className="connective-line" style={{animationDelay:'.2s'}} />
                <path d="M 400 430 Q 200 320 160 140" stroke="#0077B6" strokeWidth="2" fill="none" className="connective-line" style={{animationDelay:'.4s'}} />

                {nodes.map((n, i) => {
                  const cx = n.id === "demand" ? 160 : n.id === "knowledge" ? 640 : 400;
                  const cy = n.id === "delivery" ? 430 : 140;
                  return (
                    <g key={n.id}>
                      <circle cx={cx} cy={cy} r="44" fill={n.color} opacity="0.12" className="pulse-node" style={{animationDelay:`${i*.3}s`}}/>
                      <circle cx={cx} cy={cy} r="22" fill={n.color} />
                      <circle cx={cx} cy={cy} r="6" fill="#fff" />
                      <text x={cx} y={cy+70} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="11" letterSpacing="3" fill="#0B172A">{n.label}</text>
                    </g>
                  );
                })}

                <text x="400" y="300" textAnchor="middle" fontFamily="Fraunces" fontStyle="italic" fontSize="22" fill="#0B172A" opacity="0.55">UNNATI</text>
                <text x="400" y="325" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9" letterSpacing="3" fill="#0B172A" opacity="0.55">CONNECTIVE LAYER</text>
              </svg>
            </div>
            <div className="grid grid-cols-3 border-x border-b border-navy/10 bg-white text-xs">
              {[
                ["Desiloed", "working layer"],
                ["Competency-linked", "supply"],
                ["Reusable", "across network"],
              ].map(([k, v]) => (
                <div key={k} className="p-4 border-r last:border-r-0 border-navy/10">
                  <div className="font-mono-data uppercase tracking-[0.18em] text-navy/50">{k}</div>
                  <div className="mt-1 text-navy">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
